/* eslint-disable ssr-friendly/no-dom-globals-in-react-fc */
import * as React from 'react';
import styled from 'styled-components';
import { capitalize, groupBy } from 'lodash';
import MiniSearch, { SearchResult } from 'minisearch';
import { Link, navigate } from 'gatsby';
import { tokens, Modal, HStack, useWindowWidth, EmptyState, Box, Button } from '@carta/ink';
import index from './search-index.json';
import StatusBubble from '../Common/StatusBubble';
import { TABLET_PORTRAIT_BREAKPOINT } from '../IntroPages/styles';

import StyledText from '../Common/StyledText';
import { useCloseOnEscape } from '../../hooks/useCloseOnEscape';

const CATEGORIES = ['components', 'patterns', 'guidelines', 'foundations', 'props'] as const;
type Category = typeof CATEGORIES[number];

const SearchModalStyleWrapper = styled.div`
  * {
    font-family: 'Plus Jakarta Sans', sans-serif !important;
  }

  /* padding on modalwrapper is 24px 32px 32px 32px */
  /* we need to get to 40px */
  padding: 16px 8px 8px 8px;

  /* Mobile scrolling fix - aligns with Ink's tabletPortrait breakpoint */
  @media (max-width: ${TABLET_PORTRAIT_BREAKPOINT}) {
    margin-top: 8px;
    max-height: calc(100vh - 48px); /* fallback for browsers without dvh support */
    max-height: 100dvh;
    overflow-y: auto;
    /* -webkit-overflow-scrolling: touch; */
    overscroll-behavior: contain;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  height: 44px;
  border-radius: ${tokens.size.global.radius.default};
  padding: 9px 15px 9px 46px;
  border: 1px solid #d2d2d2;

  color: #1a1a1a;
  font-size: 16px;
  line-height: 28px;
  font-weight: 400;

  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.820962 6.32668C0.820962 3.30675 3.21817 0.843559 6.15722 0.843559C9.09626 0.843559 11.4935 3.30675 11.4935 6.32668C11.4935 9.34662 9.09626 11.8098 6.15722 11.8098C3.21817 11.8098 0.820962 9.34662 0.820962 6.32668ZM7.40753e-09 6.32668C3.31857e-09 9.81901 2.75843 12.6534 6.15722 12.6534C9.556 12.6534 12.3144 9.81901 12.3144 6.32668C12.3144 2.83435 9.556 2.51332e-07 6.15722 4.03987e-07C2.75843 5.56641e-07 1.14965e-08 2.83436 7.40753e-09 6.32668Z' fill='black'/%3E%3Cpath d='M9.9785 10.8534L14.9883 16.001L15.5688 15.4045L10.559 10.2569L9.9785 10.8534Z' fill='black'/%3E%3C/svg%3E%0A");
  background-repeat: no-repeat;
  background-position-y: 50%;
  background-position-x: 15px;
`;

const ResultCategory = styled.div<{ type: Category }>`
  border-inline-start: 8px solid;
  padding-inline-start: 16px;

  &[type='components'] {
    border-inline-start-color: ${tokens.color.global.brand['blue-20']};
  }
  &[type='patterns'] {
    border-inline-start-color: #e1ddf2;
  }
  &[type='guidelines'] {
    border-inline-start-color: ${tokens.color.global.brand['brown-20']};
  }
  &[type='foundations'] {
    border-inline-start-color: ${tokens.color.global.brand['green-20']};
  }
  &[type='props'] {
    border-inline-start-color: ${tokens.color.global.brand['gray-30']};
  }
`;

const ResultTile = styled(Link)`
  border: 1px solid #dedfdf;
  border-radius: 4px;
  padding: 12px;
  text-decoration: none;
  min-width: 0; /* Allow grid items to shrink below content size */

  :hover {
    border-color: #1a1a1a;
    text-decoration: inherit;
  }
`;

const ResultGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 8px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

/* fields should match scripts/scrape/writers/search-index.js */
const searchIndex = MiniSearch.loadJS(index, {
  fields: ['displayName', 'description', 'prop', 'compiledMd', 'accessibilityMd', 'contentful', 'status'],
  storeFields: ['displayName', 'url', 'category', 'status', 'description'],
  searchOptions: {
    boostDocument(documentId, term, storedFields) {
      if (!storedFields) {
        /* this shouldn't happen ever, but we gotta make TS happy */
        return 1;
      }

      /* deprioritize results from deprecated components */
      const isDeprioritized = ['sunset', 'deprecated'].includes(storedFields.status as string);
      if (isDeprioritized) {
        return -10;
      }

      const normalizedName = (storedFields.displayName as string).toLowerCase();
      const normalizedTerm = term.toLowerCase();

      // Ultra boost: component name matches query or new+query
      if (normalizedName === normalizedTerm || normalizedName === `new${normalizedTerm}`) {
        return 500;
      }

      // High boost: first part of name contains the term
      const firstPart = normalizedName.split('.')[0];
      if (firstPart.includes(normalizedTerm)) {
        return 100;
      }

      return 1;
    },
    combineWith: 'AND',
    boost: {
      displayName: 30,
      description: 10,
      prop: 20,
    },
    fuzzy: 0.2, // Enable fuzzy search
    prefix: true, // Enable prefix search
  },
});

const isDeprecated = ({ status }: { status: string }) => (['sunset', 'deprecated'].includes(status) ? 1 : -1);

// prettier-ignore
// A list of fake search results for the first load
const DEFAULT_SEARCH_RESULTS = (Object.values(index.storedFields)
  .filter(({ category }) => category === 'component')
  .sort((a, b) => a.displayName.localeCompare(b.displayName))
  .sort((a, b) => isDeprecated(a) - isDeprecated(b))
  .map(c => ({ ...c, match: { fakematch: ['displayName'] } })) as unknown) as SearchResult[];

const useSyncQueryStringState = (value: string) => {
  React.useEffect(() => {
    const url = new URL(window.location.href);
    if (value) {
      url.searchParams.set('search', value);
    } else {
      url.searchParams.delete('search');
    }
    window.history.replaceState({}, '', url.toString());
  }, [value]);
};

// NewTable.Cell => newtablecell
const removeDot = (s: string) => s.replace(/\./g, '').toLowerCase();

type SearchState = {
  error: string;
  results: SearchResult[];
  query: string;
  expanded: Record<string, boolean>;
};

type SearchAction =
  | { type: 'SET_QUERY'; payload: string }
  | { type: 'SET_RESULTS'; payload: { results: SearchResult[]; error: string } }
  | { type: 'RESET_EXPANDED' }
  | { type: 'TOGGLE_EXPANDED'; payload: string };

const searchReducer = (state: SearchState, action: SearchAction): SearchState => {
  switch (action.type) {
    case 'SET_QUERY':
      return { ...state, query: action.payload };
    case 'SET_RESULTS':
      return { ...state, results: action.payload.results, error: action.payload.error };
    case 'RESET_EXPANDED':
      return { ...state, expanded: state.query ? {} : { components: true } };
    case 'TOGGLE_EXPANDED': {
      const category = action.payload;
      return {
        ...state,
        expanded: {
          ...state.expanded,
          [category]: state.expanded[category] !== undefined ? !state.expanded[category] : true,
        },
      };
    }
    default:
      return state;
  }
};

const useAdvancedSearch = ({ getInitialValue }: { getInitialValue: () => string }) => {
  const [state, dispatch] = React.useReducer(searchReducer, {
    error: '',
    results: DEFAULT_SEARCH_RESULTS,
    query: getInitialValue(),
    expanded: { components: true },
  });

  React.useEffect(() => {
    if (!state.query) {
      dispatch({ type: 'SET_RESULTS', payload: { results: DEFAULT_SEARCH_RESULTS, error: '' } });
      dispatch({ type: 'RESET_EXPANDED' });
      return;
    }

    try {
      const results = searchIndex.search(state.query);
      dispatch({ type: 'SET_RESULTS', payload: { results, error: '' } });
      dispatch({ type: 'RESET_EXPANDED' });
    } catch (e: any) {
      /* avoid minisearch bug when searching for letter `m` */
      if (e.message.includes('Invalid regular expression')) {
        dispatch({ type: 'SET_RESULTS', payload: { results: DEFAULT_SEARCH_RESULTS, error: '' } });
        return;
      }

      dispatch({ type: 'SET_RESULTS', payload: { results: [], error: e.message } });
    }
  }, [state.query]);

  return {
    query: state.query,
    error: state.error,
    searchResults: state.results,
    expanded: state.expanded,
    expandResults: (category: string) => {
      dispatch({ type: 'TOGGLE_EXPANDED', payload: category });
    },
    getInputProps: () => ({
      value: state.query,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({ type: 'SET_QUERY', payload: e.target.value });
      },
    }),
  };
};

const categoryMap: Record<string, Category> = {
  hooks: 'components',
  component: 'components',
  accessibility: 'guidelines',
  deprecation: 'guidelines',
  contributing: 'guidelines',
  guides: 'guidelines',
  'usage guides': 'guidelines',
  'migration guides': 'guidelines',
  internationalization: 'guidelines',
  'ink linter': 'guidelines',
  forms: 'patterns',
  patterns: 'patterns',
  foundations: 'foundations',
};
interface Props {
  close: () => void;
}

const getInitialSearchValue = () => new URL(window.location.href).searchParams.get('search') || '';

export const SiteSearchModal = (props: Props) => {
  const { query, searchResults, error, getInputProps, expanded, expandResults } = useAdvancedSearch({
    getInitialValue: getInitialSearchValue,
  });

  const { isMobile, isTabletPortrait } = useWindowWidth();

  const handleClose = React.useCallback(() => {
    // Clear search query from URL
    const url = new URL(window.location.href);
    url.searchParams.delete('search');
    window.history.replaceState({}, '', url.toString());
    props.close();
  }, [props]);

  useCloseOnEscape(handleClose);

  useSyncQueryStringState(query);

  const groupedResults = groupBy(searchResults, obj => categoryMap[obj.category as Category]);

  const sortedResults = Object.fromEntries(
    Object.entries(groupedResults).sort(([a, b]) => (a === 'components' ? -1 : 1)),
  );

  return (
    <Modal enableNewStyles width={isMobile ? '95%' : '50vw'} onRequestClose={handleClose}>
      <SearchModalStyleWrapper id="modal-wrapper">
        {/* Show close button on mobile/tablet portrait - aligns with Ink's tabletPortrait breakpoint */}
        {(isMobile || isTabletPortrait) && <Modal.Header title="Search" onClose={handleClose} />}
        <Modal.Body>
          <SearchInput
            id="search"
            autoFocus
            placeholder="Search for components, guides, props..."
            {...getInputProps()}
          />
          <Box top="10">
            {query && searchResults.length > 0 && (
              <StyledText variant="small-1">{`${searchResults.length} results for "${query}"`}</StyledText>
            )}
          </Box>
          {!searchResults.length && query.length > 0 && (
            <>
              <EmptyState type="block" icon="notfound" text={`No results found for "${query}"`}>
                Try a different term, or browse for <Link to="/components">components</Link> or{' '}
                <Link to="/guides">guides</Link>
              </EmptyState>
            </>
          )}
          {Object.entries(sortedResults).map(([category, results]) => (
            <React.Fragment key={category}>
              <Box top="16" bottom="4">
                <StyledText weight="bold">{capitalize(category)}</StyledText>
              </Box>
              <ResultCategory type={category as Category}>
                <ResultGrid>
                  {(expanded[category] ? results : results.slice(0, 16)).map(result => (
                    <ResultTile
                      key={result.url}
                      to={result.url}
                      onClick={handleClose}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          navigate(result.url);
                          handleClose();
                        }
                      }}
                    >
                      <HStack align="left" spacing="4">
                        <StyledText>{result.displayName}</StyledText>
                        {result.status && <StatusBubble status={result.status} />}
                      </HStack>
                      <StyledText variant="small-1">{result.description}</StyledText>
                    </ResultTile>
                  ))}
                </ResultGrid>
                {results.length > 16 && !expanded[category] && (
                  <Button onClick={() => expandResults(category)} type="link">
                    See more...
                  </Button>
                )}
              </ResultCategory>
            </React.Fragment>
          ))}
        </Modal.Body>
      </SearchModalStyleWrapper>
    </Modal>
  );
};
